import React, { useEffect, useRef, useState } from "react";
import api from "../../services/api";

const DEBOUNCE_MS = 200;

export default function SearchAutoSuggest({ value, onChange, onPick, className = "" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const term = String(value || "").trim();
    if (term.length < 2) {
      setItems([]);
      setOpen(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.getPaginatedLeads({ search: term }, 1, 5);
        const rows = Array.isArray(res?.data) ? res.data : [];
        setItems(rows.slice(0, 5));
        setOpen(rows.length > 0);
        setHighlight(0);
      } catch {
        setItems([]);
        setOpen(false);
      }
    }, DEBOUNCE_MS);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [value]);

  const onKeyDown = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = items[highlight];
      if (pick) {
        setOpen(false);
        onPick?.(pick);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className={`r6-search-autosuggest ${className}`.trim()}>
      <i className="fas fa-search r6-search-icon" />
      <input
        className="form-control"
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Search leads, contacts, email, phone…"
      />
      {value ? (
        <button
          type="button"
          className="r6-search-clear"
          onClick={() => onChange?.("")}
          aria-label="Clear search"
        >
          <i className="fas fa-times" />
        </button>
      ) : null}
      {open && items.length > 0 && (
        <div className="r6-autosuggest-menu">
          {items.map((it, i) => {
            const contact = Array.isArray(it.contacts) && it.contacts[0];
            const contactName = contact
              ? [contact.first_name, contact.last_name].filter(Boolean).join(" ")
              : "";
            return (
              <button
                key={it.lead_id}
                type="button"
                className={`r6-autosuggest-item ${i === highlight ? "is-highlighted" : ""}`}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  onPick?.(it);
                }}
              >
                <span className="r6-autosuggest-company">{it.company}</span>
                {contactName && <span className="r6-autosuggest-contact">{contactName}</span>}
                <span className="r6-autosuggest-meta">
                  {[it.lead_source, it.partner_name].filter(Boolean).join(" · ")}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}